(function() {
  'use strict';

  const config = window.GA4CustomEventsConfig;
  const pageContext = window.GA4PageContext || {};

  // ============================================================================
  // SESSION CORRELATION UTILITIES
  // ============================================================================

  /**
   * Get GA4 client ID from _ga cookie for session correlation
   * This allows behavioral events to be joined with purchase events in GA4
   */
  function getGA4ClientId() {
    try {
      const gaCookie = document.cookie.split('; ').find(function(row) {
        return row.startsWith('_ga=');
      });
      if (gaCookie) {
        // _ga cookie format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
        const parts = gaCookie.split('.');
        if (parts.length >= 4) {
          return parts[2] + '.' + parts[3];
        }
      }
    } catch (e) {}
    return null;
  }

  /**
   * Get GA4 session ID from _ga_XXXXXX cookie
   */
  function getGA4SessionId() {
    try {
      const sessionCookie = document.cookie.split('; ').find(function(row) {
        return row.startsWith('_ga_');
      });
      if (sessionCookie) {
        // Format: GS1.1.SESSION_ID.SESSION_COUNT...
        const parts = sessionCookie.split('.');
        if (parts.length >= 3) {
          return parts[2];
        }
      }
    } catch (e) {}
    return null;
  }

  /**
   * Get session correlation data for all custom events
   * This enables joining behavioral events with purchase events in GA4 Explorations
   */
  function getSessionCorrelation() {
    return {
      ga_client_id: getGA4ClientId(),
      ga_session_id: getGA4SessionId(),
      page_location: window.location.href,
      page_title: document.title
    };
  }

  /**
   * Safely call closest() on event target
   * Handles text nodes, SVG elements, and other non-Element targets
   */
  function safeClosest(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  /**
   * Numeric Shopify ID from a number, numeric string, or GID
   * ("gid://shopify/ProductVariant/123" -> "123"). Empty string when there is none.
   */
  function numericId(id) {
    if (!id) return '';
    const match = String(id).match(/(\d+)$/);
    return match ? match[1] : '';
  }

  // ============================================================================
  // GA4 TRACKING HELPER
  // ============================================================================

  var gpuBucket = null;

  // virtual_gpu: the unmasked WebGL renderer names SwiftShader, which headless Chrome reports.
  // physical_gpu: a renderer was found. gpu_not_found: no WebGL, the extension is blocked, or it threw.
  function getGpuBucket() {
    if (gpuBucket) return gpuBucket;
    gpuBucket = 'gpu_not_found';
    try {
      var canvas = document.createElement('canvas');
      var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        var info = gl.getExtension('WEBGL_debug_renderer_info');
        var renderer = info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : '';
        if (renderer) gpuBucket = /swiftshader/i.test(String(renderer)) ? 'virtual_gpu' : 'physical_gpu';
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      }
    } catch (e) {}
    return gpuBucket;
  }

  var GA4CustomEvents = {
    initialized: false,
    trackedEvents: new Set(), // Prevent duplicate events within short timeframe
    variantSkuMap: {}, // Map of variant ID to SKU for lookup

    /**
     * Initialize and set up tracking
     */
    init: function() {
      // Check if gtag is available (loaded by Analyzify)
      if (typeof gtag !== 'function') {
        console.warn('[GA4 Custom] gtag() not found - events will not be tracked. Ensure Analyzify is enabled.');
        return;
      }
      this.setupTracking();
      this.initialized = true;
      this.log('GA4 Custom Events initialized (using gtag direct)');
    },

    /**
     * Debug logging
     */
    log: function() {
      if (config.debug) {
        console.log.apply(console, ['[GA4 Custom]'].concat(Array.prototype.slice.call(arguments)));
      }
    },

    /**
     * Send ecommerce event directly to GA4 via gtag()
     */
    pushEvent: function(eventName, eventParams) {
      // Dedupe check - prevent same event within 500ms
      const eventKey = eventName + JSON.stringify(eventParams);
      if (this.trackedEvents.has(eventKey)) {
        this.log('Skipping duplicate event:', eventName);
        return;
      }

      this.trackedEvents.add(eventKey);
      setTimeout(function() {
        GA4CustomEvents.trackedEvents.delete(eventKey);
      }, 500);

      // Send directly to GA4 via gtag()
      if (typeof gtag === 'function') {
        eventParams = Object.assign({}, eventParams, { gpu_bucket: getGpuBucket() });
        gtag('event', eventName, eventParams);
        this.log('Event sent via gtag:', eventName, eventParams);
      }
    },

    /**
     * Send non-ecommerce custom event directly to GA4 via gtag()
     * Session data (ga_client_id, ga_session_id) enables joining with purchase events
     */
    pushCustomEvent: function(eventName, eventParams) {
      const sessionData = getSessionCorrelation();
      const params = Object.assign({}, sessionData, eventParams, { gpu_bucket: getGpuBucket() });

      // Send directly to GA4 via gtag()
      if (typeof gtag === 'function') {
        gtag('event', eventName, params);
        this.log('Custom event sent via gtag:', eventName, params);
      }
    },

    /**
     * Build GA4 item object from product data
     */
    buildItem: function(data) {
      const item = {
        item_id: String(data.id || data.product_id || ''),
        item_name: data.title || data.name || data.product_title || '',
        price: this.normalizePrice(data.price),
        quantity: data.quantity || 1
      };

      if (data.variant_id) item.item_variant = String(data.variant_id);
      if (data.variant_title) item.item_variant = data.variant_title;
      if (data.sku) item.item_id = data.sku; // Prefer SKU as item_id
      if (data.brand || data.vendor) item.item_brand = data.brand || data.vendor;
      if (data.type || data.category) item.item_category = data.type || data.category;
      if (data.handle) item.item_id = item.item_id || data.handle;
      if (data.index !== undefined) item.index = data.index;

      return item;
    },

    /**
     * Normalize price to number (in dollars)
     * @param {number|string} price - Price value
     * @param {boolean} isCents - If true, price is in cents (Shopify variant.price)
     */
    normalizePrice: function(price, isCents) {
      if (typeof price === 'number') {
        // Shopify variant.price is always in cents
        return isCents ? price / 100 : price;
      }
      if (typeof price === 'string') {
        // Parse price from DOM text (already formatted as dollars)
        return parseFloat(price.replace(/[^0-9.]/g, '')) || 0;
      }
      return 0;
    },

    /**
     * Extract product data from element
     */
    extractProductFromElement: function(element) {
      const card = element.closest('[data-product-id], [data-product-handle], .product-card, .card-product');
      if (!card) return null;

      const titleEl = card.querySelector('.card-product__title, .product-card__title, h3, h4');
      const priceEl = card.querySelector('[data-price], .price');

      return {
        id: card.dataset.productId || '',
        handle: card.dataset.productHandle || '',
        title: titleEl ? titleEl.textContent.trim() : '',
        price: priceEl ? priceEl.textContent.trim() : '',
        vendor: card.dataset.vendor || ''
      };
    },

    /**
     * Extract product data from URL
     */
    extractProductFromUrl: function(url) {
      const match = url.match(/\/products\/([^/?#]+)/);
      return match ? { handle: match[1] } : null;
    },

    /**
     * GA4 item for one variant, rendered by sections/ga4-item-data.liquid so the brand
     * comes from the variant. Resolves to null when the render fails.
     */
    itemRequests: {},
    fetchWishlistItem: function(handle, variantId) {
      const key = handle + ':' + (variantId || '');
      if (this.itemRequests[key]) return this.itemRequests[key];

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = root + 'products/' + encodeURIComponent(handle) + '?' +
        (variantId ? 'variant=' + encodeURIComponent(variantId) + '&' : '') +
        'section_id=ga4-item-data';
      this.itemRequests[key] = fetch(url)
        .then(function(response) {
          if (!response.ok) throw new Error('ga4-item-data ' + response.status);
          return response.text();
        })
        .then(function(html) {
          const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ga4-item]');
          return el ? JSON.parse(el.textContent) : null;
        })
        .catch(function(err) {
          delete GA4CustomEvents.itemRequests[key];
          GA4CustomEvents.log('Wishlist item lookup failed:', handle, err);
          return null;
        });
      return this.itemRequests[key];
    },

    /**
     * The WishlistKing app embed loads after this script; wait up to 10s for it.
     */
    whenWishlistKing: function(callback) {
      let attempts = 0;
      (function check() {
        const wk = window.WishlistKing;
        if (wk && wk.events) {
          callback(wk);
        } else if (++attempts < 100) {
          setTimeout(check, 100);
        }
      })();
    },

    /**
     * read_buying_guide fires once per page view, after 30s on the guide or a scroll past
     * half of it, whichever comes first.
     */
    setupGuideRead: function() {
      const self = this;
      let remaining = 30000;
      let startedAt = 0;
      let timer = null;
      let done = false;

      function read() {
        if (done) return;
        done = true;
        clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
        self.pushCustomEvent('read_buying_guide', pageContext.guide);
      }

      // Half of what can be scrolled, so a guide shorter than two screens isn't "read" on
      // arrival.
      function onScroll() {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable > 0 && window.scrollY >= scrollable / 2) read();
      }

      // Time in a background tab doesn't count.
      function startTimer() {
        if (done || document.hidden) return;
        clearTimeout(timer);
        startedAt = Date.now();
        timer = setTimeout(read, remaining);
      }
      document.addEventListener('visibilitychange', function() {
        if (document.hidden) {
          clearTimeout(timer);
          if (startedAt) remaining = Math.max(0, remaining - (Date.now() - startedAt));
          startedAt = 0;
        } else {
          startTimer();
        }
      });

      startTimer();
      window.addEventListener('scroll', onScroll, { passive: true });
    },

    /**
     * Safely get text content from element
     */
    getText: function(el) {
      return el ? el.textContent.trim() : '';
    },

    /**
     * Safely get value from element
     */
    getValue: function(el) {
      return el ? el.value : '';
    },

    /**
     * Read Algolia search attribution from sessionStorage if present and unexpired.
     * Written by theme/assets/algolia-cart-attribution.js on every search-result click
     * (main results page + predictive search), with the same 30-min TTL.
     */
    getSearchAttribution: function() {
      try {
        const raw = sessionStorage.getItem('_algolia_attribution');
        if (!raw) return null;
        const data = JSON.parse(raw);
        const TTL_MS = 30 * 60 * 1000;
        if (!data || !data.query || !data.timestamp) return null;
        if (Date.now() - data.timestamp > TTL_MS) return null;
        return data;
      } catch (e) {
        return null;
      }
    },

    // ============================================================================
    // TRACKING SETUP
    // ============================================================================

    setupTracking: function() {
      // Standard ecommerce events: scoped strictly to the search-attribution path.
      // Both fire ONLY when there's a `?q=<keyword>` (view_item_list) or
      // sessionStorage._algolia_attribution (add_to_cart). All other paths
      // (collection, featured-section, recommendations) stay disabled to
      // avoid duplicating Analyzify — see commit 71479ac.
      this.setupViewItemListTracking();   // Search results page only
      this.setupAddToCartTracking();      // Search-attributed ATCs only
      this.setupSearchResultClickTracking(); // select_item on search-result clicks
      if (!config.designMode) {
        this.setupMenuTracking();
      }
      this.setupNotFoundTracking();
      this.setupCollectionPageTracking();
      this.setupProductPageTracking();
      this.setupChatTracking();
      this.setupCopyTracking();
      this.setupTier1ProductDiscovery();
      this.setupTier2PurchaseIntent();
      this.setupTier3DeepEngagement();
      this.setupTier4CartPromo();
      this.setupTier5NavigationDiscovery();
    },

    // ============================================================================
    // ADD TO CART TRACKING WITH PRODUCT LIST CONTEXT
    // ============================================================================

    /**
     * Track add-to-cart events ONLY when the user came from a search result.
     *
     * Analyzify's GTM container fires `add_to_cart` for every ATC; re-emitting
     * unconditionally would inflate counts. We fire a parallel `add_to_cart`
     * stamped with `item_list_name: 'Search: <keyword>'` so GA4 / Looker Studio
     * can attribute revenue to a search keyword. Filter `item_list_name LIKE
     * 'Search:%'` in reports to count once and avoid double-counting Analyzify.
     */
    setupAddToCartTracking: function() {
      const self = this;

      function buildSearchContext() {
        const attr = self.getSearchAttribution();
        if (!attr) return null;
        return { listId: 'search_results', listName: 'Search: ' + attr.query };
      }

      // Listen for successful add-to-cart events (drawer/notification cart types)
      document.addEventListener('product-ajax:added', function(e) {
        const context = buildSearchContext();
        if (!context) return;

        const product = (e.detail && e.detail.product) || {};
        const item = {
          item_id: product.sku || String(product.variant_id || product.id || ''),
          item_name: product.product_title || product.title || '',
          item_variant: product.variant_title || product.title || '',
          price: self.normalizePrice(product.price, true),
          quantity: product.quantity || 1
        };

        if (product.brand || product.vendor) item.item_brand = product.brand || product.vendor;
        if (product.product_type) item.item_category = product.product_type;

        self.pushEvent('add_to_cart', {
          currency: config.currency,
          value: item.price * item.quantity,
          item_list_id: context.listId,
          item_list_name: context.listName,
          items: [item]
        });
      });

      // Also track form submissions directly (for forms not using product-ajax:added)
      document.addEventListener('submit', function(e) {
        const form = safeClosest(e.target, 'form[action*="/cart/add"], product-form');
        if (!form) return;

        // Only track if this form doesn't trigger product-ajax:added (page cart type)
        if (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.cartType !== 'page') return;

        const context = buildSearchContext();
        if (!context) return;

        const variantInput = form.querySelector('[name="id"]');
        const quantityInput = form.querySelector('[name="quantity"]');
        const variantId = variantInput ? variantInput.value : '';
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        let productName = '';
        let productPrice = 0;
        if (pageContext.product) {
          productName = pageContext.product.title;
          productPrice = pageContext.product.price;
        }

        self.pushEvent('add_to_cart', {
          currency: config.currency,
          value: productPrice * quantity,
          item_list_id: context.listId,
          item_list_name: context.listName,
          items: [{
            item_id: self.variantSkuMap[variantId] || String(variantId),
            item_name: productName,
            price: productPrice,
            quantity: quantity
          }]
        });
      }, true);

      self.log('Add to Cart tracking initialized (search-attribution only)');
    },

    /**
     * Fire `select_item` when a search-result link is clicked, stamped with
     * `item_list_name: 'Search: <keyword>'`. Covers both the main /search page
     * and predictive-search dropdown clicks (the latter detected via the
     * sessionStorage attribution that algolia-cart-attribution.js writes).
     */
    setupSearchResultClickTracking: function() {
      const self = this;
      const SEARCH_RESULT_SELECTORS = [
        '.predictive-search__result-link',
        '.template-search .card-product a[href*="/products/"]',
        '.search-results .card-product a[href*="/products/"]',
        '.ais-Hits-item a[href*="/products/"]',
        '[data-algolia-hit] a[href*="/products/"]'
      ].join(', ');

      document.addEventListener('click', function(e) {
        const link = safeClosest(e.target, SEARCH_RESULT_SELECTORS);
        if (!link) return;

        // Resolve the search keyword: URL on /search, sessionStorage for predictive
        let keyword = '';
        if (pageContext.template === 'search') {
          keyword = new URLSearchParams(window.location.search).get('q') || '';
        }
        if (!keyword) {
          const attr = self.getSearchAttribution();
          if (attr && attr.query) keyword = attr.query;
        }
        if (!keyword) return;

        const card = safeClosest(link, '.card-product, .product-card, .ais-Hits-item, [data-algolia-hit]');
        const titleEl = card ? card.querySelector('.card-product__title, .product-card__title, h3, h4, [class*="title"]') : null;
        const handleMatch = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        const siblings = card && card.parentElement ? Array.prototype.indexOf.call(card.parentElement.children, card) : -1;

        const item = {
          item_id: (card && (card.dataset.productId || card.dataset.productHandle)) || (handleMatch ? handleMatch[1] : ''),
          item_name: titleEl ? titleEl.textContent.trim() : ''
        };
        if (siblings >= 0) item.index = siblings;
        if (!item.item_id && !item.item_name) return;

        self.pushEvent('select_item', {
          item_list_id: 'search_results',
          item_list_name: 'Search: ' + keyword,
          items: [item]
        });
      }, true);

      self.log('Search-result click tracking initialized');
    },

    /**
     * Determine the product list context from an element
     */
    getAddToCartContext: function(element) {
      if (!element) return this.getDefaultATCContext();

      // Check for explicit data attribute first
      const explicitList = element.closest('[data-product-list]');
      if (explicitList) {
        return {
          listId: explicitList.dataset.productList,
          listName: this.formatListName(explicitList.dataset.productList)
        };
      }

      // Quick View modal
      if (element.closest('quick-view-modal, .quick-view-modal')) {
        return { listId: 'quick_view', listName: 'Quick View' };
      }

      // Complete The Set
      if (element.closest('.complete-the-set, [class*="complete-the-set"]')) {
        return { listId: 'complete_the_set', listName: 'Complete The Set' };
      }

      // Cart drawer recommendations
      if (element.closest('.cart-drawer__products-recommendation, .cart-recommendations')) {
        return { listId: 'cart_recommendations', listName: 'Cart Recommendations' };
      }

      // Product recommendations section
      if (element.closest('.product-recommendations, [data-product-recommendations]')) {
        return { listId: 'product_recommendations', listName: 'Product Recommendations' };
      }

      // Related products
      if (element.closest('.related-products, [class*="related-product"]')) {
        return { listId: 'related_products', listName: 'Related Products' };
      }

      // Recently viewed
      if (element.closest('.recently-viewed-products, [class*="recently-viewed"]')) {
        return { listId: 'recently_viewed', listName: 'Recently Viewed' };
      }

      // Featured collection section
      if (element.closest('.featured-collection, [class*="featured-collection"]')) {
        const sectionEl = element.closest('[data-section-id], section');
        const sectionTitle = this.getText(sectionEl && sectionEl.querySelector('h2, .section-heading'));
        return {
          listId: 'featured_collection',
          listName: sectionTitle || 'Featured Collection'
        };
      }

      // Collection page (Algolia-rendered)
      if (element.closest('.ais-Hits, .algolia-hits, [class*="algolia"]')) {
        const collectionTitle = pageContext.collection ? pageContext.collection.title : '';
        return {
          listId: 'collection_algolia',
          listName: collectionTitle ? collectionTitle + ' (Search)' : 'Collection (Algolia)'
        };
      }

      // Collection page (native)
      if (element.closest('.collection-product-grid, .main-collection, #MainCollection')) {
        const collTitle = pageContext.collection ? pageContext.collection.title : '';
        return {
          listId: 'collection',
          listName: collTitle || 'Collection'
        };
      }

      // Search results
      if (element.closest('.search-results, .template-search, [class*="search-result"]')) {
        return { listId: 'search_results', listName: 'Search Results' };
      }

      // Sticky add-to-cart bar
      if (element.closest('.sticky-atc, .sticky-add-to-cart, [class*="sticky-atc"]')) {
        return { listId: 'sticky_atc', listName: 'Sticky Add to Cart' };
      }

      // Product bundles
      if (element.closest('.products-bundle, [class*="bundle"]')) {
        return { listId: 'product_bundle', listName: 'Product Bundle' };
      }

      // Main product form on PDP
      if (element.closest('.product-form, .main-product, product-info')) {
        return { listId: 'product_page', listName: 'Product Page' };
      }

      // Fallback based on page template
      return this.getDefaultATCContext();
    },

    /**
     * Get default context based on current page
     */
    getDefaultATCContext: function() {
      if (pageContext.template === 'product') {
        return { listId: 'product_page', listName: 'Product Page' };
      }
      if (pageContext.template === 'collection') {
        const collTitle = pageContext.collection ? pageContext.collection.title : '';
        return { listId: 'collection', listName: collTitle || 'Collection' };
      }
      if (pageContext.template === 'search') {
        return { listId: 'search_results', listName: 'Search Results' };
      }
      if (pageContext.template === 'cart') {
        return { listId: 'cart_page', listName: 'Cart Page' };
      }
      if (pageContext.template === 'index') {
        return { listId: 'homepage', listName: 'Homepage' };
      }
      return { listId: 'other', listName: 'Other' };
    },

    /**
     * Format list ID into readable list name
     */
    formatListName: function(listId) {
      if (!listId) return 'Unknown';
      return listId
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, function(l) { return l.toUpperCase(); });
    },

    // ============================================================================
    // VIEW ITEM LIST TRACKING (GA4 view_item_list events)
    // ============================================================================

    /**
     * Fire view_item_list on the search results page so GA4 carries the
     * actual `?q=<keyword>` in `item_list_name`. Other listing surfaces
     * (collection, featured, recommendations) are intentionally NOT covered —
     * Analyzify already fires view_item_list for those.
     */
    setupViewItemListTracking: function() {
      const self = this;
      const viewedLists = new Set(); // Prevent duplicate view events per page load

      /**
       * Extract product items from a container element
       */
      function extractProductItems(container, maxItems) {
        maxItems = maxItems || 20; // Limit to prevent huge payloads
        const items = [];
        const productCards = container.querySelectorAll('.card-product, .product-card, [data-product-id], .ais-Hits-item');

        for (let i = 0; i < Math.min(productCards.length, maxItems); i++) {
          const card = productCards[i];
          const titleEl = card.querySelector('.card-product__title, .product-card__title, h3, h4, [class*="title"]');
          const priceEl = card.querySelector('[data-price], .price, [class*="price"]');
          const linkEl = card.querySelector('a[href*="/products/"]');

          const item = {
            item_id: card.dataset.productId || card.dataset.productHandle || '',
            item_name: titleEl ? titleEl.textContent.trim() : '',
            price: priceEl ? self.normalizePrice(priceEl.textContent.trim()) : 0,
            index: i
          };

          // Try to extract handle from link if no ID
          if (!item.item_id && linkEl) {
            const match = linkEl.href.match(/\/products\/([^/?#]+)/);
            if (match) item.item_id = match[1];
          }

          if (item.item_id || item.item_name) {
            items.push(item);
          }
        }

        return items;
      }

      /**
       * Fire view_item_list event
       */
      function fireViewItemList(listId, listName, items) {
        if (!items || items.length === 0) return;

        // Dedupe check
        const listKey = listId + '_' + items.length;
        if (viewedLists.has(listKey)) return;
        viewedLists.add(listKey);

        self.pushEvent('view_item_list', {
          currency: config.currency,
          item_list_id: listId,
          item_list_name: listName,
          items: items
        });

        self.log('view_item_list fired:', listName, '(' + items.length + ' items)');
      }

      // -------------------------------------------------------------------------
      // Search Results Page - Track on page load
      //
      // SCOPE: Only the search-results path is enabled. Collection-page,
      // featured-section, cart-drawer, and dynamic-Algolia paths were removed
      // (commit 71479ac disabled them to stop duplicating Analyzify). This
      // single path stays because Analyzify does NOT stamp the actual `?q=`
      // keyword onto item_list_name — without it, GA4 cannot attribute revenue
      // back to a search term.
      // -------------------------------------------------------------------------
      if (pageContext.template === 'search') {
        setTimeout(function() {
          const searchResults = document.querySelector('.search-results, .template-search__results, .ais-Hits');
          if (!searchResults) return;

          const searchQuery = new URLSearchParams(window.location.search).get('q') || '';
          if (!searchQuery) return; // No keyword → nothing to attribute

          const items = extractProductItems(searchResults);
          fireViewItemList('search_results', 'Search: ' + searchQuery, items);
        }, 500);
      }

      // -------------------------------------------------------------------------
      // Dynamic Content - Re-track when Algolia hydrates the search results page
      // (the initial setTimeout above can run before Algolia paints its hits).
      // Scoped to template === 'search' so non-search Algolia widgets are ignored.
      // -------------------------------------------------------------------------
      if (pageContext.template === 'search' && typeof MutationObserver !== 'undefined') {
        const searchHydrationObserver = new MutationObserver(function() {
          const target = document.querySelector('.ais-Hits-list, .algolia-hits, .ais-InfiniteHits-list');
          if (!target || target.children.length === 0) return;

          clearTimeout(self._searchViewTimeout);
          self._searchViewTimeout = setTimeout(function() {
            const searchQuery = new URLSearchParams(window.location.search).get('q') || '';
            if (!searchQuery) return;

            // Clear dedupe key so we re-fire when hit count changes
            viewedLists.delete('search_results_' + target.querySelectorAll('.ais-Hits-item').length);

            const items = extractProductItems(target);
            if (items.length > 0) {
              fireViewItemList('search_results', 'Search: ' + searchQuery, items);
            }
          }, 500);
        });

        const algoliaContainers = document.querySelectorAll('.ais-Hits, .ais-InfiniteHits');
        algoliaContainers.forEach(function(container) {
          searchHydrationObserver.observe(container, { childList: true, subtree: true });
        });
      }

      self.log('View Item List tracking initialized (search-only)');
    },

    // ============================================================================
    // MENU/NAVIGATION TRACKING (opens, clicks, abandons)
    // ============================================================================

    /**
     * Track menu interactions: opens, clicks, and abandons
     * Helps understand navigation behavior and menu effectiveness
     */
    setupMenuTracking: function() {
      // Under menu v5 none of this runs (setupMenuV5Tracking counts v5). v5 keeps the
      // <menu-drawer> shell, so the drawer listener below would count every v5 click too.
      if (document.querySelector('header[data-menu-version="v5"]')) return;

      const self = this;

      function pushMenuEvent(eventName, params) {
        params.menu_version = 'legacy';
        self.pushCustomEvent(eventName, params);
      }
      const currentMenuState = {
        isOpen: false,
        menuName: null,
        openTime: null,
        hasClicked: false
      };

      // The department dropdowns in the desktop nav, mega or plain. The drawer's details also
      // use level="top", so this stays inside .header__menu.
      const TOP_LEVEL_SELECTOR = '.header__menu details[level="top"]';
      let activeDropdown = null;

      function getMenuName(details) {
        const summary = details.querySelector(':scope > summary');
        const name = summary ? summary.textContent.replace(/\s+/g, ' ').trim() : '';
        return name || 'Unknown Menu';
      }

      /**
       * The department, then each details the element sits in ("Inspiration > Shop By Room").
       * Mega menu columns are details that are always open, so this walks up from the
       * element instead of listing every open details on the page.
       */
      function getMenuContext(el) {
        const path = [];
        let details = safeClosest(el, 'details');
        while (details) {
          path.unshift(getMenuName(details));
          if (details.matches(TOP_LEVEL_SELECTOR)) break;
          details = safeClosest(details.parentElement, 'details');
        }
        return path.join(' > ') || 'Unknown';
      }

      function startVisit(details) {
        activeDropdown = details;
        currentMenuState.isOpen = true;
        currentMenuState.menuName = getMenuName(details);
        currentMenuState.openTime = Date.now();
        currentMenuState.hasClicked = false;

        pushMenuEvent('menu_open', {
          menu_name: currentMenuState.menuName,
          menu_context: getMenuContext(details),
          page_template: pageContext.template
        });
      }

      function endVisit() {
        if (!currentMenuState.hasClicked) {
          pushMenuEvent('menu_abandon', {
            menu_name: currentMenuState.menuName,
            time_open_ms: Date.now() - currentMenuState.openTime,
            page_template: pageContext.template
          });
        }

        activeDropdown = null;
        currentMenuState.isOpen = false;
        currentMenuState.menuName = null;
        currentMenuState.openTime = null;
        currentMenuState.hasClicked = false;
      }

      // -------------------------------------------------------------------------
      // 1. Track Menu Opens - Watch each department's summary
      // -------------------------------------------------------------------------
      // DetailsDropdown (header.js) sets and clears its summary's open attribute the moment it
      // opens or closes; the details' own open attribute stays until the close animation ends.
      // A move straight to another department can open the new one before the old one closes,
      // so every opening ends the visit before it and starts its own.
      const dropdownObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          const summary = mutation.target;
          if (summary.localName !== 'summary') return;
          const details = summary.parentElement;
          if (!details || !details.matches(TOP_LEVEL_SELECTOR)) return;

          if (summary.hasAttribute('open')) {
            if (details === activeDropdown) return;
            if (activeDropdown) endVisit();
            startVisit(details);
          } else if (details === activeDropdown) {
            endVisit();
          }
        });
      });

      const desktopNav = document.querySelector('.header__menu');
      if (desktopNav) {
        dropdownObserver.observe(desktopNav, {
          subtree: true,
          attributes: true,
          attributeFilter: ['open']
        });
      }

      // -------------------------------------------------------------------------
      // 2. Track Menu Clicks - Any link clicked within the dropdown menu
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        // Check if click is within an open mega-menu/dropdown
        const menuContainer = safeClosest(e.target, 'details-dropdown[open], header details[open], .mega-menu__dropdown, .details-dropdown__content');
        if (!menuContainer) return;
        // The mobile drawer's submenus are also header details[open]; section 3 counts those clicks.
        if (safeClosest(e.target, 'menu-drawer')) return;

        const link = safeClosest(e.target, 'a[href]');
        if (!link) return;

        // A link in a department that's still closing doesn't count against the visit that replaced it.
        const linkDropdown = safeClosest(link, TOP_LEVEL_SELECTOR);
        if (!linkDropdown || linkDropdown === activeDropdown) {
          currentMenuState.hasClicked = true;
        }

        const clickText = link.textContent.trim().replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
        const clickUrl = link.href;
        const menuName = linkDropdown ? getMenuName(linkDropdown) : currentMenuState.menuName || 'Unknown Menu';

        // Determine link type
        let linkType = 'navigation';
        if (clickUrl.includes('/collections/')) linkType = 'collection';
        else if (clickUrl.includes('/products/')) linkType = 'product';
        else if (clickUrl.includes('/pages/')) linkType = 'page';
        else if (clickUrl.includes('/blogs/')) linkType = 'blog';

        // Check if it's a promotional/featured link (usually has images or special styling)
        const isPromo = !!link.closest('.mega-menu__promo, .mega-menu__featured, .mega-menu__card, [class*="promo"], [class*="featured"]');

        pushMenuEvent('menu_click', {
          menu_name: menuName,
          menu_context: getMenuContext(link),
          click_text: clickText,
          click_url: clickUrl,
          link_type: linkType,
          is_promotional: isPromo,
          time_to_click_ms: currentMenuState.openTime ? Date.now() - currentMenuState.openTime : 0,
          page_template: pageContext.template
        });

      }, true); // Use capture to get clicks before navigation

      // -------------------------------------------------------------------------
      // 3. Track Mobile Menu Interactions
      // -------------------------------------------------------------------------
      const mobileMenuDrawer = document.querySelector('menu-drawer');
      if (mobileMenuDrawer) {
        let mobileMenuOpen = false;
        let mobileMenuOpenTime = null;
        let mobileMenuClicked = false;

        const mobileMenuObserver = new MutationObserver(function() {
          const isOpen = mobileMenuDrawer.classList.contains('active') ||
                       mobileMenuDrawer.classList.contains('is-open') ||
                       mobileMenuDrawer.hasAttribute('open');

          if (isOpen && !mobileMenuOpen) {
            // Mobile menu opened
            mobileMenuOpen = true;
            mobileMenuOpenTime = Date.now();
            mobileMenuClicked = false;

            pushMenuEvent('menu_open', {
              menu_name: 'Mobile Menu',
              menu_context: 'Mobile Navigation',
              page_template: pageContext.template
            });

          } else if (!isOpen && mobileMenuOpen) {
            // Mobile menu closed
            const duration = Date.now() - (mobileMenuOpenTime || Date.now());

            if (!mobileMenuClicked) {
              pushMenuEvent('menu_abandon', {
                menu_name: 'Mobile Menu',
                time_open_ms: duration,
                page_template: pageContext.template
              });
            }

            mobileMenuOpen = false;
            mobileMenuOpenTime = null;
            mobileMenuClicked = false;
          }
        });

        mobileMenuObserver.observe(mobileMenuDrawer, {
          attributes: true,
          attributeFilter: ['class', 'open']
        });

        // Track mobile menu link clicks
        mobileMenuDrawer.addEventListener('click', function(e) {
          const link = safeClosest(e.target, 'a[href]');
          if (!link) return;

          mobileMenuClicked = true;

          const clickText = link.textContent.trim().replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

          pushMenuEvent('menu_click', {
            menu_name: 'Mobile Menu',
            click_text: clickText,
            click_url: link.href,
            time_to_click_ms: mobileMenuOpenTime ? Date.now() - mobileMenuOpenTime : 0,
            page_template: pageContext.template
          });
        }, true);
      }

      // -------------------------------------------------------------------------
      // 4. Track Menu Scroll (if menu content is scrollable)
      // -------------------------------------------------------------------------
      const menuScrollTracked = {};
      document.addEventListener('scroll', function(e) {
        if (!currentMenuState.isOpen) return;

        const scrollableMenu = safeClosest(e.target, '.mega-menu__dropdown, .details-dropdown__content, .mega-menu__list');
        if (!scrollableMenu) return;

        const menuId = currentMenuState.menuName || 'unknown';
        if (menuScrollTracked[menuId]) return;

        menuScrollTracked[menuId] = true;

        pushMenuEvent('menu_scroll', {
          menu_name: currentMenuState.menuName,
          scroll_top: e.target.scrollTop,
          page_template: pageContext.template
        });
      }, true);

      // -------------------------------------------------------------------------
      // 5. Track Menu Search (if search exists within menu)
      // -------------------------------------------------------------------------
      document.addEventListener('submit', function(e) {
        const menuSearch = safeClosest(e.target, '.mega-menu__search, .menu-search, [class*="menu"] [class*="search"]');
        if (!menuSearch) return;

        const searchInput = menuSearch.querySelector('input[type="search"], input[type="text"]');
        const query = searchInput ? searchInput.value : '';

        pushMenuEvent('menu_search', {
          menu_name: currentMenuState.menuName || 'Menu',
          search_query: query,
          page_template: pageContext.template
        });
      }, true);

      // Also track on input for instant search
      let menuSearchDebounce = null;
      document.addEventListener('input', function(e) {
        const menuSearchInput = safeClosest(e.target, '.mega-menu__search input, .menu-search input');
        if (!menuSearchInput) return;

        clearTimeout(menuSearchDebounce);
        menuSearchDebounce = setTimeout(function() {
          if (menuSearchInput.value.length >= 3) {
            pushMenuEvent('menu_search', {
              menu_name: currentMenuState.menuName || 'Menu',
              search_query: menuSearchInput.value,
              search_type: 'instant',
              page_template: pageContext.template
            });
          }
        }, 1000);
      }, true);

      // -------------------------------------------------------------------------
      // 6. Track Mobile Menu Back Button Clicks
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const backBtn = safeClosest(e.target, '.drawer-menu__back, .mobile-menu__back, [class*="menu"] [class*="back"], .menu-back-button');
        if (!backBtn) return;

        // The drawer's back button is labeled with the level it leaves ("< Lamps").
        const currentLevel = backBtn.textContent.replace(/\s+/g, ' ').trim();

        pushMenuEvent('menu_back_click', {
          menu_name: 'Mobile Menu',
          from_level: currentLevel || 'Unknown',
          page_template: pageContext.template
        });
      }, true);

      // -------------------------------------------------------------------------
      // 7. Track Menu Category Expand (accordions/expandable sections)
      // -------------------------------------------------------------------------
      document.addEventListener('toggle', function(e) {
        if (!e.target.matches('details')) return;

        const menuDetails = safeClosest(e.target, '.mega-menu details, .drawer-menu details, header details details, .mobile-menu details');
        if (!menuDetails) return;
        if (menuDetails === safeClosest(e.target, 'details-dropdown, header > nav > details')) return; // Skip top-level

        const categoryName = self.getText(menuDetails.querySelector('summary'));

        pushMenuEvent('menu_category_expand', {
          menu_name: currentMenuState.menuName || 'Mobile Menu',
          category_name: categoryName,
          is_expanded: menuDetails.open,
          page_template: pageContext.template
        });
      }, true);

      self.log('Menu tracking initialized');
    },

    // ============================================================================
    // 404 PAGE TRACKING
    // ============================================================================

    /**
     * Fire page_not_found with the requested path so GA4 can surface which
     * URLs 404 (Magento-era paths, retired collections, bad inbound links).
     * Skipped for /f/ filter URLs — main-404.liquid client-redirects those
     * to the parent collection before the visitor sees the page.
     */
    setupNotFoundTracking: function() {
      if (pageContext.template !== '404') return;
      if (/^(.+)\/f\/[\w-]+--/.test(window.location.pathname)) return;

      this.pushCustomEvent('page_not_found', {
        not_found_path: window.location.pathname + window.location.search,
        referrer: document.referrer || '(none)'
      });
    },

    // ============================================================================
    // COLLECTION PAGE TRACKING
    // ============================================================================

    /**
     * Track collection page interactions: hero clicks, filters, pagination, etc.
     */
    setupCollectionPageTracking: function() {
      const self = this;

      // Only run on collection and search pages
      if (pageContext.template !== 'collection' && pageContext.template !== 'search') {
        return;
      }

      const collectionContext = {
        handle: (pageContext.collection && pageContext.collection.handle) || '',
        title: (pageContext.collection && pageContext.collection.title) || ''
      };

      // -------------------------------------------------------------------------
      // 1. Collection Hero/Banner Click Tracking
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const heroLink = safeClosest(e.target,
          '.collection-hero a, .collection-banner a, .main-collection-banner a, ' +
          '.collection-header a, [class*="collection-hero"] a, [class*="collection-banner"] a'
        );
        if (!heroLink) return;

        const heroSection = heroLink.closest('.collection-hero, .collection-banner, .main-collection-banner, .collection-header');
        const heading = self.getText(heroSection && heroSection.querySelector('h1, h2, .collection-hero__title'));

        self.pushCustomEvent('collection_hero_click', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          hero_heading: heading,
          click_text: self.getText(heroLink),
          click_url: heroLink.href
        });
      }, true);

      // -------------------------------------------------------------------------
      // 2. Filter Drawer/Sidebar Open Tracking
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const filterToggle = safeClosest(e.target,
          '.facets__open, .filter-toggle, [data-filter-toggle], ' +
          '[class*="filter"] button[class*="toggle"], .collection-filters__toggle'
        );
        if (!filterToggle) return;

        self.pushCustomEvent('filter_open', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title
        });
      });

      // Also track filter drawer via mutation
      const filterDrawer = document.querySelector('.facets-drawer, .filter-drawer, [class*="filter-drawer"]');
      if (filterDrawer) {
        const filterDrawerObserver = new MutationObserver(function() {
          const isOpen = filterDrawer.classList.contains('active') ||
                       filterDrawer.classList.contains('is-open') ||
                       filterDrawer.hasAttribute('open');
          if (isOpen) {
            self.pushCustomEvent('filter_open', {
              collection_handle: collectionContext.handle,
              collection_title: collectionContext.title,
              filter_type: 'drawer'
            });
          }
        });
        filterDrawerObserver.observe(filterDrawer, { attributes: true, attributeFilter: ['class', 'open'] });
      }

      // -------------------------------------------------------------------------
      // 3. Filter Apply Tracking (when filter values are selected)
      // -------------------------------------------------------------------------
      document.addEventListener('change', function(e) {
        const filterInput = safeClosest(e.target,
          '.facets__item input, .filter-option input, [data-filter-input], ' +
          '.facet-checkbox input, .ais-RefinementList-checkbox'
        );
        if (!filterInput) return;

        const filterGroup = filterInput.closest('.facets__group, .filter-group, .facet-group, .ais-RefinementList');
        const filterName = self.getText(filterGroup && filterGroup.querySelector('.facets__title, .filter-title, .facet-title, h3, h4'));
        const filterValue = filterInput.value || self.getText(filterInput.closest('label'));

        self.pushCustomEvent('filter_apply', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          filter_name: filterName,
          filter_value: filterValue,
          is_checked: filterInput.checked
        });
      });

      // Track Algolia refinement clicks
      document.addEventListener('click', function(e) {
        const algoliaFilter = safeClosest(e.target, '.ais-RefinementList-item, .ais-Menu-item, .ais-HierarchicalMenu-item');
        if (!algoliaFilter) return;

        const filterGroup = algoliaFilter.closest('.ais-RefinementList, .ais-Menu, .ais-HierarchicalMenu');
        const filterName = self.getText(filterGroup && filterGroup.closest('[class*="facet"]')?.querySelector('h3, h4, .facet-title'));
        const filterValue = self.getText(algoliaFilter.querySelector('.ais-RefinementList-labelText, .ais-Menu-label'));

        self.pushCustomEvent('filter_apply', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          filter_name: filterName || 'Algolia Filter',
          filter_value: filterValue,
          filter_type: 'algolia'
        });
      });

      // -------------------------------------------------------------------------
      // 4. Filter Clear/Reset Tracking
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const clearBtn = safeClosest(e.target,
          '.facets__clear, .filter-clear, [data-filter-clear], .clear-filters, ' +
          '.ais-ClearRefinements-button, [class*="clear-filter"], .facets__reset'
        );
        if (!clearBtn) return;

        // Check if clearing single filter or all
        const filterGroup = clearBtn.closest('.facets__group, .filter-group');
        const filterName = filterGroup ? self.getText(filterGroup.querySelector('.facets__title, .filter-title')) : 'all';

        self.pushCustomEvent('filter_clear', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          filter_cleared: filterName
        });
      });

      // -------------------------------------------------------------------------
      // 5. Pagination/Load More Click Tracking
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        // Pagination number clicks
        const pageLink = safeClosest(e.target, '.pagination a, .pagination__item a, [class*="pagination"] a');
        if (pageLink) {
          const pageNum = self.getText(pageLink) || 'unknown';
          const isPrev = pageLink.classList.contains('prev') || pageLink.classList.contains('pagination__prev');
          const isNext = pageLink.classList.contains('next') || pageLink.classList.contains('pagination__next');

          self.pushCustomEvent('pagination_click', {
            collection_handle: collectionContext.handle,
            collection_title: collectionContext.title,
            page_type: isPrev ? 'previous' : isNext ? 'next' : 'number',
            page_value: pageNum
          });
          return;
        }

        // Load more button clicks
        const loadMoreBtn = safeClosest(e.target,
          '.load-more, [data-load-more], .collection-load-more, ' +
          '.ais-InfiniteHits-loadMore, [class*="load-more"]'
        );
        if (loadMoreBtn) {
          self.pushCustomEvent('pagination_click', {
            collection_handle: collectionContext.handle,
            collection_title: collectionContext.title,
            page_type: 'load_more'
          });
        }
      });

      // -------------------------------------------------------------------------
      // 6. View Mode Toggle (Grid vs List)
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const viewToggle = safeClosest(e.target,
          '.view-toggle, [data-view-mode], .grid-list-toggle, ' +
          '[class*="view-toggle"], [class*="grid-toggle"], [class*="list-toggle"]'
        );
        if (!viewToggle) return;

        const viewMode = viewToggle.dataset.viewMode ||
                       (viewToggle.classList.contains('grid') || viewToggle.classList.contains('view-grid') ? 'grid' : 'list');

        self.pushCustomEvent('view_mode_change', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          view_mode: viewMode
        });
      });

      // -------------------------------------------------------------------------
      // 7. Product Card Hover/Dwell Tracking
      // -------------------------------------------------------------------------
      const hoverTimers = new WeakMap();
      const hoveredProducts = new Set();

      // Touch devices fire mouseenter/mouseleave on tap and never produce real
      // hover dwell — skip these capture-phase listeners on (hover: none) so they
      // don't run on every collection-grid tap (mobile INP).
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      document.addEventListener('mouseenter', function(e) {
        const productCard = safeClosest(e.target, '.card-product, .product-card, [data-product-id], .ais-Hits-item');
        if (!productCard) return;

        const productId = productCard.dataset.productId || productCard.dataset.productHandle || '';
        if (hoveredProducts.has(productId)) return; // Only track once per session

        hoverTimers.set(productCard, {
          startTime: Date.now(),
          productId: productId
        });
      }, true);

      document.addEventListener('mouseleave', function(e) {
        const productCard = safeClosest(e.target, '.card-product, .product-card, [data-product-id], .ais-Hits-item');
        if (!productCard) return;

        const timerData = hoverTimers.get(productCard);
        if (!timerData) return;

        const dwellTime = Date.now() - timerData.startTime;

        // Only track meaningful hovers (> 500ms)
        if (dwellTime > 500) {
          hoveredProducts.add(timerData.productId);

          const titleEl = productCard.querySelector('.card-product__title, .product-card__title, h3, h4');

          self.pushCustomEvent('product_card_hover', {
            collection_handle: collectionContext.handle,
            collection_title: collectionContext.title,
            product_id: timerData.productId,
            product_name: titleEl ? titleEl.textContent.trim() : '',
            dwell_time_ms: dwellTime
          });
        }

        hoverTimers.delete(productCard);
      }, true);
      }

      // -------------------------------------------------------------------------
      // 8. Quick Add Click Tracking
      // -------------------------------------------------------------------------
      document.addEventListener('click', function(e) {
        const quickAddBtn = safeClosest(e.target,
          '.quick-add, .quick-add-btn, [data-quick-add], .card-product__quick-add, ' +
          '.product-card__quick-add, [class*="quick-add"]'
        );
        if (!quickAddBtn) return;

        const productCard = quickAddBtn.closest('.card-product, .product-card, [data-product-id]');
        const productId = productCard ? (productCard.dataset.productId || productCard.dataset.productHandle) : '';
        const titleEl = productCard && productCard.querySelector('.card-product__title, .product-card__title, h3, h4');

        self.pushCustomEvent('quick_add_click', {
          collection_handle: collectionContext.handle,
          collection_title: collectionContext.title,
          product_id: productId,
          product_name: titleEl ? titleEl.textContent.trim() : ''
        });
      }, true);

      // -------------------------------------------------------------------------
      // 9. Scroll Depth Tracking (25%, 50%, 75%, 100%)
      // -------------------------------------------------------------------------
      const scrollMilestones = { 25: false, 50: false, 75: false, 100: false };
      const productGrid = document.querySelector('.collection-product-grid, .main-collection, #MainCollection, .ais-Hits');

      if (productGrid) {
        var scrollDepthObserver = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (!entry.isIntersecting) return;

            const milestone = parseInt(entry.target.dataset.scrollMilestone);
            if (scrollMilestones[milestone]) return;

            scrollMilestones[milestone] = true;

            self.pushCustomEvent('collection_scroll_depth', {
              collection_handle: collectionContext.handle,
              collection_title: collectionContext.title,
              scroll_depth_percent: milestone
            });

            scrollDepthObserver.unobserve(entry.target);
          });
        }, { threshold: 0.1 });

        // Create invisible marker elements at scroll milestones
        setTimeout(function() {
          const gridHeight = productGrid.scrollHeight;
          const gridTop = productGrid.offsetTop;

          [25, 50, 75, 100].forEach(function(milestone) {
            const marker = document.createElement('div');
            marker.dataset.scrollMilestone = milestone;
            marker.style.cssText = 'position:absolute;height:1px;width:1px;pointer-events:none;';
            marker.style.top = (gridTop + (gridHeight * milestone / 100)) + 'px';
            marker.style.left = '0';
            document.body.appendChild(marker);
            scrollDepthObserver.observe(marker);
          });
        }, 1000); // Delay to ensure grid is rendered
      }

      self.log('Collection page tracking initialized');
    },

    // ============================================================================
    // PRODUCT PAGE (PDP) TRACKING
    // ============================================================================

    /**
     * Track live-chat opens (all pages). olark-integration.liquid dispatches
     * 'olark:chat-open' on every gesture-driven box expansion, carrying the
     * originating trigger in detail.source ('launcher', 'chat_link', or a
     * 'pdp_*' value from the rs-ask card).
     */
    setupChatTracking: function() {
      const self = this;
      window.addEventListener('olark:chat-open', function(e) {
        self.pushCustomEvent('chat_open', {
          source: (e.detail && e.detail.source) || 'launcher',
          page_template: pageContext.template
        });
      });
    },

    /**
     * Track product page interactions: images, info, reviews, purchase intent
     */
    setupProductPageTracking: function() {
      const self = this;

      // Only run on product pages
      if (pageContext.template !== 'product') {
        return;
      }

      const productContext = {
        id: (pageContext.product && pageContext.product.id) || '',
        handle: (pageContext.product && pageContext.product.handle) || '',
        title: (pageContext.product && pageContext.product.title) || '',
        price: (pageContext.product && pageContext.product.price) || 0
      };

      // -------------------------------------------------------------------------
      // 1. Image Gallery Engagement
      // -------------------------------------------------------------------------

      // Track image swipes/navigation
      let lastImageIndex = 0;
      const imageViewCounts = {};

      // Swiper/slider navigation
      document.addEventListener('click', function(e) {
        const navBtn = safeClosest(e.target,
          '.product__media-nav .swiper-button-next, .product__media-nav .swiper-button-prev, ' +
          '.product-gallery .swiper-button-next, .product-gallery .swiper-button-prev, ' +
          '.product__media .swiper-pagination-bullet'
        );
        if (!navBtn) return;

        const direction = navBtn.classList.contains('swiper-button-next') ? 'next' :
                        navBtn.classList.contains('swiper-button-prev') ? 'prev' : 'dot';

        self.pushCustomEvent('product_image_swipe', {
          product_id: productContext.id,
          product_name: productContext.title,
          direction: direction
        });
      });

      // Track Swiper slide changes
      const productSwiper = document.querySelector('.product__media .swiper, .product-gallery .swiper');
      if (productSwiper && productSwiper.swiper) {
        productSwiper.swiper.on('slideChange', function() {
          const index = this.activeIndex;
          imageViewCounts[index] = (imageViewCounts[index] || 0) + 1;

          self.pushCustomEvent('product_image_view', {
            product_id: productContext.id,
            product_name: productContext.title,
            image_position: index + 1,
            view_count: imageViewCounts[index]
          });

          lastImageIndex = index;
        });
      }

      // Track thumbnail clicks
      document.addEventListener('click', function(e) {
        const thumbnail = safeClosest(e.target,
          '.product__media-thumbnail, .product-thumbnail, [data-thumbnail], ' +
          '.product__media-item--thumb, .media-gallery__thumb'
        );
        if (!thumbnail) return;

        const thumbnailIndex = Array.from(thumbnail.parentElement.children).indexOf(thumbnail);

        self.pushCustomEvent('product_thumbnail_click', {
          product_id: productContext.id,
          product_name: productContext.title,
          thumbnail_position: thumbnailIndex + 1
        });
      });

      // -------------------------------------------------------------------------
      // 2. Product Info Engagement
      // -------------------------------------------------------------------------

      // Track description read (scrolled into view)
      let descriptionTracked = false;
      const descriptionEl = document.querySelector('.product__description, .product-description, [data-product-description]');
      if (descriptionEl) {
        var descriptionObserver = new IntersectionObserver(function(entries) {
          if (entries[0].isIntersecting && !descriptionTracked) {
            descriptionTracked = true;
            self.pushCustomEvent('product_description_read', {
              product_id: productContext.id,
              product_name: productContext.title
            });
            descriptionObserver.disconnect();
          }
        }, { threshold: 0.5 });
        descriptionObserver.observe(descriptionEl);
      }

      // Track shipping info view
      document.addEventListener('click', function(e) {
        const shippingTrigger = safeClosest(e.target,
          '[data-shipping-info], .shipping-info-trigger, [class*="shipping"] summary, ' +
          '[class*="delivery"] summary, .product__shipping-toggle'
        );
        if (!shippingTrigger) return;

        self.pushCustomEvent('shipping_info_view', {
          product_id: productContext.id,
          product_name: productContext.title
        });
      });

      // Track returns policy view
      document.addEventListener('click', function(e) {
        const returnsTrigger = safeClosest(e.target,
          '[data-returns-info], .returns-info-trigger, [class*="return"] summary, ' +
          '[class*="refund"] summary, .product__returns-toggle'
        );
        if (!returnsTrigger) return;

        self.pushCustomEvent('returns_policy_view', {
          product_id: productContext.id,
          product_name: productContext.title
        });
      });

      // Track "read more" clicks
      document.addEventListener('click', function(e) {
        const readMoreBtn = safeClosest(e.target,
          '.read-more, [data-read-more], .show-more, [class*="read-more"], ' +
          '.truncate-toggle, .description-toggle'
        );
        if (!readMoreBtn) return;

        const section = readMoreBtn.closest('[class*="description"], [class*="details"], section');
        const sectionName = section ? (section.dataset.section || section.className.split(' ')[0]) : 'unknown';

        self.pushCustomEvent('read_more_click', {
          product_id: productContext.id,
          product_name: productContext.title,
          section: sectionName
        });
      });

      // -------------------------------------------------------------------------
      // 3. Reviews & Social Proof Engagement
      // -------------------------------------------------------------------------

      // Track review sort/filter
      document.addEventListener('change', function(e) {
        const reviewSort = safeClosest(e.target,
          '.reviews-sort, [data-review-sort], .stamped-sort select, ' +
          '.yotpo-sort select, [class*="review"] select'
        );
        if (!reviewSort) return;

        self.pushCustomEvent('review_sort', {
          product_id: productContext.id,
          product_name: productContext.title,
          sort_value: reviewSort.value
        });
      });

      // Track review photo view
      document.addEventListener('click', function(e) {
        const reviewPhoto = safeClosest(e.target,
          '.review-photo, .stamped-photo, .yotpo-review-image, ' +
          '[class*="review"] img, .customer-photo'
        );
        if (!reviewPhoto) return;

        self.pushCustomEvent('review_photo_view', {
          product_id: productContext.id,
          product_name: productContext.title
        });
      });

      // Track review helpful votes
      document.addEventListener('click', function(e) {
        const helpfulBtn = safeClosest(e.target,
          '.review-helpful, .stamped-helpful, .yotpo-helpful, ' +
          '[data-review-vote], [class*="helpful"], [class*="vote"]'
        );
        if (!helpfulBtn) return;

        const voteType = helpfulBtn.classList.contains('unhelpful') ||
                       helpfulBtn.dataset.vote === 'down' ? 'unhelpful' : 'helpful';

        self.pushCustomEvent('review_helpful_vote', {
          product_id: productContext.id,
          product_name: productContext.title,
          vote_type: voteType
        });
      });

      // Track "write a review" click
      document.addEventListener('click', function(e) {
        const writeReviewBtn = safeClosest(e.target,
          '.write-review, [data-write-review], .stamped-write-review, ' +
          '.yotpo-write-review, [class*="write-review"]'
        );
        if (!writeReviewBtn) return;

        self.pushCustomEvent('write_review_click', {
          product_id: productContext.id,
          product_name: productContext.title
        });
      });

      // Track Q&A question ask
      // No substring class match here: [class*="ask-question"] also matched
      // the .ask-question-modal wrapper, counting every click inside the
      // opened modal as another ask. isTrusted guard: the PDP rs-ask trigger
      // forwards to the LDC widget's own button via ldcBtn.click(), which
      // would otherwise count the same ask twice.
      document.addEventListener('click', function(e) {
        if (!e.isTrusted) return;
        const askQuestionBtn = safeClosest(e.target,
          '.ask-question, [data-ask-question], [data-rs-ask-trigger], ' +
          '.qa-ask-btn, .product-qa__ask'
        );
        if (!askQuestionBtn) return;

        self.pushCustomEvent('qa_question_ask', {
          product_id: productContext.id,
          product_name: productContext.title,
          source: askQuestionBtn.getAttribute('data-rs-ask-source') || 'default',
          chat_state: document.body.getAttribute('data-olark-state') || 'unknown'
        });
      });

      // -------------------------------------------------------------------------
      // 4. Purchase Intent Signals
      // -------------------------------------------------------------------------

      // Track quantity changes
      document.addEventListener('click', function(e) {
        const qtyBtn = safeClosest(e.target,
          '.quantity__button, .qty-btn, [data-quantity-btn], ' +
          '.product-form__quantity button, [class*="quantity"] button'
        );
        if (!qtyBtn) return;

        const qtyInput = qtyBtn.closest('.quantity, [class*="quantity"]')?.querySelector('input');
        const currentQty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
        const direction = qtyBtn.classList.contains('minus') ||
                        qtyBtn.classList.contains('decrease') ||
                        qtyBtn.dataset.action === 'decrease' ||
                        qtyBtn.name === 'minus' ? 'decrease' : 'increase';

        self.pushCustomEvent('quantity_change', {
          product_id: productContext.id,
          product_name: productContext.title,
          direction: direction,
          new_quantity: direction === 'increase' ? currentQty + 1 : Math.max(1, currentQty - 1)
        });
      });

      // Also track direct input changes
      document.addEventListener('change', function(e) {
        const qtyInput = safeClosest(e.target,
          '.quantity__input, .qty-input, [data-quantity-input], ' +
          '.product-form__quantity input, input[name="quantity"]'
        );
        if (!qtyInput) return;

        self.pushCustomEvent('quantity_change', {
          product_id: productContext.id,
          product_name: productContext.title,
          direction: 'direct_input',
          new_quantity: parseInt(qtyInput.value) || 1
        });
      });

      // Track "notify me" / back-in-stock clicks
      document.addEventListener('click', function(e) {
        const notifyBtn = safeClosest(e.target,
          '.notify-me, [data-notify-me], .back-in-stock, ' +
          '[class*="notify"], [class*="waitlist"], .klaviyo-bis-trigger'
        );
        if (!notifyBtn) return;

        self.pushCustomEvent('notify_me_click', {
          product_id: productContext.id,
          product_name: productContext.title
        });
      });

      // Track sticky ATC visibility
      const stickyAtc = document.querySelector(
        '.sticky-atc, .sticky-add-to-cart, [data-sticky-atc], ' +
        '[class*="sticky-atc"], .product-sticky-bar'
      );
      if (stickyAtc) {
        let stickyTracked = false;
        const stickyObserver = new IntersectionObserver(function(entries) {
          if (entries[0].isIntersecting && !stickyTracked) {
            stickyTracked = true;
            self.pushCustomEvent('sticky_atc_view', {
              product_id: productContext.id,
              product_name: productContext.title
            });
          }
        }, { threshold: 0.1 });
        stickyObserver.observe(stickyAtc);
      }

      // -------------------------------------------------------------------------
      // 5. Sharing & Trust
      // -------------------------------------------------------------------------

      // Track share button clicks
      document.addEventListener('click', function(e) {
        const shareBtn = safeClosest(e.target,
          '.share-button, [data-share], .social-share a, ' +
          '[class*="share-"], .product-share a'
        );
        if (!shareBtn) return;

        let platform = 'unknown';
        const href = shareBtn.href || '';
        const classList = shareBtn.className || '';

        if (href.includes('facebook') || classList.includes('facebook')) platform = 'facebook';
        else if (href.includes('twitter') || href.includes('x.com') || classList.includes('twitter')) platform = 'twitter';
        else if (href.includes('pinterest') || classList.includes('pinterest')) platform = 'pinterest';
        else if (href.includes('email') || href.includes('mailto') || classList.includes('email')) platform = 'email';
        else if (href.includes('whatsapp') || classList.includes('whatsapp')) platform = 'whatsapp';
        else if (classList.includes('copy') || shareBtn.dataset.action === 'copy') platform = 'copy_link';

        self.pushCustomEvent('product_share', {
          product_id: productContext.id,
          product_name: productContext.title,
          share_platform: platform
        });
      });

      // Track trust badge clicks
      document.addEventListener('click', function(e) {
        const trustBadge = safeClosest(e.target,
          '.trust-badge, [data-trust-badge], .guarantee-badge, ' +
          '[class*="trust"], [class*="guarantee"], .product-badges a'
        );
        if (!trustBadge) return;

        const badgeText = self.getText(trustBadge);

        self.pushCustomEvent('trust_badge_click', {
          product_id: productContext.id,
          product_name: productContext.title,
          badge_text: badgeText
        });
      });

      // Track payment options view
      const paymentOptions = document.querySelector(
        '.payment-options, .payment-icons, [data-payment-options], ' +
        '[class*="payment-method"], .shopify-payment-button__more-options'
      );
      if (paymentOptions) {
        let paymentTracked = false;
        var paymentObserver = new IntersectionObserver(function(entries) {
          if (entries[0].isIntersecting && !paymentTracked) {
            paymentTracked = true;
            self.pushCustomEvent('payment_options_view', {
              product_id: productContext.id,
              product_name: productContext.title
            });
            paymentObserver.disconnect();
          }
        }, { threshold: 0.5 });
        paymentObserver.observe(paymentOptions);
      }

      // -------------------------------------------------------------------------
      // 6. PDP Scroll Depth Tracking
      // -------------------------------------------------------------------------
      const pdpScrollMilestones = { 25: false, 50: false, 75: false, 100: false };
      const productMain = document.querySelector(
        '.main-product, .product-page, #MainProduct, ' +
        '[data-section-type="product"], .shopify-section--product'
      );

      if (productMain) {
        var pdpScrollObserver = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (!entry.isIntersecting) return;

            const milestone = parseInt(entry.target.dataset.pdpScrollMilestone);
            if (pdpScrollMilestones[milestone]) return;

            pdpScrollMilestones[milestone] = true;

            self.pushCustomEvent('pdp_scroll_depth', {
              product_id: productContext.id,
              product_name: productContext.title,
              scroll_depth_percent: milestone
            });

            pdpScrollObserver.unobserve(entry.target);
          });
        }, { threshold: 0.1 });

        // Create invisible marker elements
        setTimeout(function() {
          const pageHeight = productMain.scrollHeight;
          const pageTop = productMain.offsetTop;

          [25, 50, 75, 100].forEach(function(milestone) {
            const marker = document.createElement('div');
            marker.dataset.pdpScrollMilestone = milestone;
            marker.style.cssText = 'position:absolute;height:1px;width:1px;pointer-events:none;';
            marker.style.top = (pageTop + (pageHeight * milestone / 100)) + 'px';
            marker.style.left = '0';
            document.body.appendChild(marker);
            pdpScrollObserver.observe(marker);
          });
        }, 1000);
      }

      self.log('Product page tracking initialized');
    },

    // ============================================================================
    // GLOBAL TEXT COPY TRACKING
    // ============================================================================

    /**
     * Track when users copy text from the page
     * Useful for understanding what content users find valuable
     */
    setupCopyTracking: function() {
      const self = this;

      document.addEventListener('copy', function(e) {
        const selection = window.getSelection();
        const copiedText = selection ? selection.toString().trim() : '';

        if (!copiedText || copiedText.length < 3) return; // Ignore tiny selections

        // Determine the context of what was copied
        const anchorNode = selection.anchorNode;
        const container = anchorNode ? (anchorNode.nodeType === 3 ? anchorNode.parentElement : anchorNode) : null;

        let copyContext = 'unknown';
        let sectionName = '';

        if (container) {
          // Determine what section the copy came from
          if (container.closest('.product__description, .product-description, [data-product-description]')) {
            copyContext = 'product_description';
          } else if (container.closest('.product__title, .product-title, h1')) {
            copyContext = 'product_title';
          } else if (container.closest('.price, [class*="price"]')) {
            copyContext = 'price';
          } else if (container.closest('.sku, [class*="sku"]')) {
            copyContext = 'sku';
          } else if (container.closest('.review, [class*="review"]')) {
            copyContext = 'review';
          } else if (container.closest('.shipping, [class*="shipping"]')) {
            copyContext = 'shipping_info';
          } else if (container.closest('.returns, [class*="return"]')) {
            copyContext = 'returns_info';
          } else if (container.closest('.product-info, .product__info')) {
            copyContext = 'product_info';
          } else if (container.closest('.collection-description')) {
            copyContext = 'collection_description';
          } else if (container.closest('.blog, .article')) {
            copyContext = 'blog_content';
          } else if (container.closest('.faq, [class*="faq"]')) {
            copyContext = 'faq';
          } else if (container.closest('nav, .menu, header')) {
            copyContext = 'navigation';
          } else if (container.closest('footer')) {
            copyContext = 'footer';
          }

          // Get the section name if available
          const section = container.closest('[data-section-id], [data-section-type], section');
          if (section) {
            sectionName = section.dataset.sectionId || section.dataset.sectionType || '';
          }
        }

        // Truncate copied text for privacy (just log first 100 chars)
        const truncatedText = copiedText.length > 100 ? copiedText.substring(0, 100) + '...' : copiedText;

        self.pushCustomEvent('text_copy', {
          copy_context: copyContext,
          section_name: sectionName,
          copied_text: truncatedText,
          text_length: copiedText.length,
          page_template: pageContext.template,
          product_id: (pageContext.product && pageContext.product.id) || '',
          product_name: (pageContext.product && pageContext.product.title) || '',
          collection_handle: (pageContext.collection && pageContext.collection.handle) || ''
        });
      });

      self.log('Copy tracking initialized');
    },

    // ============================================================================
    // TIER 1: PRODUCT DISCOVERY
    // ============================================================================

    setupTier1ProductDiscovery: function() {
      const self = this;

      // ----- Complete The Set -----
      if (config.tracking.completeTheSet) {
        // Track clicks on Complete The Set products
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.complete-the-set__card a, .complete-the-set__card-title a');
          if (!link) return;

          const card = link.closest('.complete-the-set__card');
          if (!card) return;

          const productData = {
            id: card.dataset.productId,
            handle: card.dataset.productHandle,
            title: self.getText(card.querySelector('.complete-the-set__card-title')),
            price: self.getText(card.querySelector('.complete-the-set__card-price--current'))
          };

          // DISABLED: Analyzify handles select_item events
          // self.pushEvent('select_item', {
          //   currency: config.currency,
          //   item_list_id: 'complete_the_set',
          //   item_list_name: 'Complete The Set',
          //   items: [self.buildItem(productData)]
          // });
        });

        // Track add to cart from Complete The Set
        document.addEventListener('click', function(e) {
          const btn = safeClosest(e.target, '.complete-the-set__card-add-to-cart, .complete-the-set__modal-add-to-cart');
          if (!btn || btn.disabled) return;

          const container = btn.closest('.complete-the-set__card, .complete-the-set__modal-body');
          if (!container) return;

          const variantId = btn.dataset.variantId;
          const variantSku = btn.dataset.variantSku;
          const qtyInput = container.querySelector('.complete-the-set__qty-input');
          const qty = qtyInput ? qtyInput.value : 1;

          // DISABLED: Analyzify handles add_to_cart events
          // self.pushEvent('add_to_cart', {
          //   currency: config.currency,
          //   item_list_id: 'complete_the_set',
          //   item_list_name: 'Complete The Set',
          //   items: [{
          //     item_id: variantSku || variantId,
          //     item_name: self.getText(container.querySelector('.complete-the-set__card-title, .complete-the-set__modal-title')),
          //     price: self.normalizePrice(self.getText(container.querySelector('.complete-the-set__card-price--current, .complete-the-set__modal-price'))),
          //     quantity: parseInt(qty)
          //   }]
          // });
        });
      }

      // ----- Quick View -----
      if (config.tracking.quickView) {
        document.addEventListener('quick-view:open', function(e) {
          const productUrl = e.detail && e.detail.productUrl;
          if (!productUrl) return;

          const productData = self.extractProductFromUrl(productUrl);

          // DISABLED: Analyzify handles view_item events
          // self.pushEvent('view_item', {
          //   currency: config.currency,
          //   item_list_id: 'quick_view',
          //   item_list_name: 'Quick View',
          //   items: [self.buildItem(productData)]
          // });
        });

        // Track add to cart from Quick View (using event delegation to avoid memory leak)
        document.addEventListener('submit', function(e) {
          const quickViewModal = safeClosest(e.target, 'quick-view-modal');
          if (!quickViewModal) return;
          if (!e.target.matches('[action*="/cart/add"]')) return;

          const form = e.target;
          const variantInput = form.querySelector('[name="id"]');
          const quantityInput = form.querySelector('[name="quantity"]');
          const variantId = variantInput ? variantInput.value : '';
          const quantity = quantityInput ? quantityInput.value : 1;

          // Look up SKU from variant change events, fallback to variant ID
          const variantSku = self.variantSkuMap[variantId] || String(variantId);

          // DISABLED: Analyzify handles add_to_cart events
          // self.pushEvent('add_to_cart', {
          //   currency: config.currency,
          //   item_list_id: 'quick_view',
          //   item_list_name: 'Quick View',
          //   items: [{
          //     item_id: variantSku,
          //     quantity: parseInt(quantity)
          //   }]
          // });
        }, true);
      }

      // ----- Related Products -----
      if (config.tracking.relatedProducts) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.related-products a[href*="/products/"], .product-recommendations a[href*="/products/"]');
          if (!link) return;

          const card = link.closest('.card-product, .product-card');
          const productData = card ? self.extractProductFromElement(link) : self.extractProductFromUrl(link.href);

          // DISABLED: Analyzify handles select_item events
          // self.pushEvent('select_item', {
          //   currency: config.currency,
          //   item_list_id: 'related_products',
          //   item_list_name: 'Related Products',
          //   items: [self.buildItem(productData || {})]
          // });
        });
      }

      // ----- Cart Recommendations -----
      if (config.tracking.cartRecommendations) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.cart-drawer__products-recommendation a[href*="/products/"], .cart-recommendations a[href*="/products/"]');
          if (!link) return;

          const productData = self.extractProductFromElement(link) || self.extractProductFromUrl(link.href);

          // DISABLED: Analyzify handles select_item events
          // self.pushEvent('select_item', {
          //   currency: config.currency,
          //   item_list_id: 'cart_recommendations',
          //   item_list_name: 'Cart Recommendations',
          //   items: [self.buildItem(productData || {})]
          // });
        });
      }

      // ----- Recently Viewed -----
      if (config.tracking.recentlyViewed) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.recently-viewed-products a[href*="/products/"]');
          if (!link) return;

          const productData = self.extractProductFromElement(link) || self.extractProductFromUrl(link.href);

          // DISABLED: Analyzify handles select_item events
          // self.pushEvent('select_item', {
          //   currency: config.currency,
          //   item_list_id: 'recently_viewed',
          //   item_list_name: 'Recently Viewed',
          //   items: [self.buildItem(productData || {})]
          // });
        });
      }
    },

    // ============================================================================
    // TIER 2: PURCHASE INTENT SIGNALS
    // ============================================================================

    setupTier2PurchaseIntent: function() {
      const self = this;

      // ----- Variant Selection -----
      if (config.tracking.variantSelection && typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          function(data) {
            if (!data.data || !data.data.variant) return;

            const variant = data.data.variant;

            // Store SKU for lookup during add to cart
            if (variant.sku) {
              self.variantSkuMap[variant.id] = variant.sku;
            }

            // DISABLED: Analyzify handles select_item events
            // self.pushEvent('select_item', {
            //   currency: config.currency,
            //   item_list_id: 'variant_selection',
            //   item_list_name: 'Variant Selection',
            //   items: [{
            //     item_id: variant.sku || String(variant.id),
            //     item_name: (pageContext.product && pageContext.product.title) || '',
            //     item_variant: variant.title || variant.option1,
            //     price: self.normalizePrice(variant.price, true)
            //   }]
            // });
          }
        );

        // Also listen for V2 variant selector events
        document.addEventListener('v2:variantChange', function(e) {
          if (!e.detail || !e.detail.variant) return;

          const variant = e.detail.variant;

          // Store SKU for lookup during add to cart
          if (variant.sku) {
            self.variantSkuMap[variant.id] = variant.sku;
          }

          // DISABLED: Analyzify handles select_item events
          // self.pushEvent('select_item', {
          //   currency: config.currency,
          //   item_list_id: 'variant_selection',
          //   item_list_name: 'Variant Selection',
          //   items: [{
          //     item_id: variant.sku || String(variant.id),
          //     item_name: (pageContext.product && pageContext.product.title) || '',
          //     item_variant: variant.title,
          //     price: self.normalizePrice(variant.price, true)
          //   }]
          // });
        });
      }

      // ----- Wishlist -----
      if (config.tracking.wishlist) {
        // One save reaches here up to three times: WishlistKing's add:success, then
        // wishlist-custom.js re-dispatching it, then its click handler. Only some carry the
        // variant. Collect them for a moment and send one event with the variant if any
        // carried it.
        const pendingAdds = {};
        function queueAdd(handle, variantId) {
          if (!handle) return;
          variantId = numericId(variantId);
          if (pendingAdds[handle]) {
            pendingAdds[handle].variantId = pendingAdds[handle].variantId || variantId;
            return;
          }
          pendingAdds[handle] = { variantId: variantId };
          setTimeout(function() {
            const add = pendingAdds[handle];
            delete pendingAdds[handle];
            self.fetchWishlistItem(handle, add.variantId).then(function(item) {
              if (!item) return;
              self.pushEvent('add_to_wishlist', {
                currency: config.currency,
                value: item.price,
                items: [item]
              });
            });
          }, 500);
        }

        document.addEventListener('wlk:wishlist:added', function(e) {
          const detail = e.detail || {};
          queueAdd(detail.productHandle, detail.variantId);
        });

        self.whenWishlistKing(function(wk) {
          // wishlist-custom.js reads add:success at the root and drops a save it can't find
          // a handle in, so the native Swish button may never dispatch wlk:wishlist:added.
          wk.events.subscribe('wishlist:add:success', function(event) {
            const data = (event && event.data) || event || {};
            queueAdd(
              (data.product && data.product.handle) || data.productHandle,
              (data.variant && data.variant.id) || data.variantId
            );
          });

          // remove:success fires after the item is gone, and may name only the wishlist
          // item. Remember every item seen so a removal can still be traced to a variant.
          const knownItems = {};

          wk.events.subscribe('wishlist:remove:success', function(event) {
            // Payload shape is undocumented for window.WishlistKing.events: Swish's docs
            // show it under `data`, while wishlist-custom.js reads add:success at the root.
            const data = (event && event.data) || event || {};
            const known = knownItems[data.wishlistItemId || (data.wishlistItem && data.wishlistItem.id) || data.id] || {};
            const handle = (data.product && data.product.handle) || data.productHandle || known.handle;
            const variantId = numericId((data.variant && data.variant.id) || data.variantId) || known.variantId;
            if (!handle) {
              self.log('remove_from_wishlist: no product in payload', event);
              return;
            }

            self.fetchWishlistItem(handle, variantId).then(function(item) {
              if (!item) return;
              self.pushCustomEvent('remove_from_wishlist', {
                item_id: item.item_id,
                item_variant: item.item_variant
              });
            });
          });

          // Can throw before WishlistKing has a wishlist to report (wishlist-custom.js
          // guards the same call).
          try {
            wk.state.observeWishlist({ wishlistId: 'mine' }).subscribe(function(wishlist) {
              ((wishlist && wishlist.items) || []).forEach(function(entry) {
                if (!entry.id || !entry.product || !entry.product.handle) return;
                knownItems[entry.id] = {
                  handle: entry.product.handle,
                  variantId: numericId((entry.variant && entry.variant.id) || entry.variantId)
                };
              });
            });
          } catch (err) {
            self.log('observeWishlist failed:', err);
          }
        });
      }

      // ----- Buying Guide Read -----
      if (config.tracking.buyingGuide && pageContext.guide) {
        self.setupGuideRead();
      }

      // ----- Trade Program -----
      if (config.tracking.tradeProgram && pageContext.tradeProgram) {
        self.pushCustomEvent('view_trade_program', {
          customer_type: pageContext.tradeProgram.customer_type
        });
      }

      // ----- Filters -----
      if (config.tracking.filters && typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          function(data) {
            // Parse current URL for filter info
            const params = new URLSearchParams(window.location.search);
            const filters = [];

            params.forEach(function(value, key) {
              if (key.startsWith('filter.')) {
                filters.push(key.replace('filter.', '') + ':' + value);
              }
            });

            if (filters.length === 0) return;

            self.pushCustomEvent('filter_applied', {
              filter_type: 'collection',
              filters: filters.join(', '),
              collection_handle: (pageContext.collection && pageContext.collection.handle) || '',
              collection_title: (pageContext.collection && pageContext.collection.title) || ''
            });
          }
        );
      }

      // ----- Sort Changed -----
      document.addEventListener('change', function(e) {
        if (!e.target.matches('[name="sort_by"], .facet-sort select')) return;

        self.pushCustomEvent('sort_changed', {
          sort_by: e.target.value,
          collection_handle: (pageContext.collection && pageContext.collection.handle) || '',
          collection_title: (pageContext.collection && pageContext.collection.title) || ''
        });
      });

      // ----- Compare -----
      if (config.tracking.compare) {
        document.addEventListener('compare:add', function(e) {
          const productData = e.detail || {};

          self.pushCustomEvent('add_to_compare', {
            item_id: productData.productId || '',
            item_url: productData.productUrl || ''
          });
        });

        // Compare drawer opened
        document.addEventListener('click', function(e) {
          if (!safeClosest(e.target, '.compare-bar__view-btn, [data-compare-view]')) return;

          self.pushCustomEvent('compare_view', {
            item_count: document.querySelectorAll('.compare-bar__product').length
          });
        });
      }

      // ----- Size Guide -----
      if (config.tracking.sizeGuide) {
        document.addEventListener('click', function(e) {
          const btn = safeClosest(e.target, '[data-size-guide], .size-guide-trigger, [href*="size-guide"]');
          if (!btn) return;

          self.pushCustomEvent('size_guide_open', {
            item_id: (pageContext.product && pageContext.product.id) || '',
            item_name: (pageContext.product && pageContext.product.title) || '',
            item_type: (pageContext.product && pageContext.product.type) || ''
          });
        });
      }
    },

    // ============================================================================
    // TIER 3: DEEP ENGAGEMENT
    // ============================================================================

    setupTier3DeepEngagement: function() {
      const self = this;

      // ----- Image Zoom (PhotoSwipe) -----
      if (config.tracking.imageZoom) {
        document.addEventListener('photoswipe:open', function(e) {
          self.pushCustomEvent('image_zoom', {
            item_id: (pageContext.product && pageContext.product.id) || '',
            item_name: (pageContext.product && pageContext.product.title) || '',
            image_index: (e.detail && e.detail.index) || 0
          });
        });

        // Fallback for lightbox/zoom clicks
        document.addEventListener('click', function(e) {
          const zoomTrigger = safeClosest(e.target, '.media-zoom, [data-pswp-trigger], .product__media-zoom');
          if (!zoomTrigger) return;

          self.pushCustomEvent('image_zoom', {
            item_id: (pageContext.product && pageContext.product.id) || '',
            item_name: (pageContext.product && pageContext.product.title) || ''
          });
        });
      }

      // ----- Video Play -----
      if (config.tracking.videoPlay) {
        document.addEventListener('play', function(e) {
          if (!e.target.matches('video')) return;

          const container = safeClosest(e.target, '.product__media, .video-section, [data-product-media]');

          self.pushCustomEvent('video_play', {
            item_id: (pageContext.product && pageContext.product.id) || '',
            item_name: (pageContext.product && pageContext.product.title) || '',
            video_id: e.target.dataset.videoId || e.target.src || ''
          });
        }, true);

        // YouTube/Vimeo embeds
        window.addEventListener('message', function(e) {
          try {
            const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;

            // YouTube
            if (data.event === 'onStateChange' && data.info === 1) {
              self.pushCustomEvent('video_play', {
                item_id: (pageContext.product && pageContext.product.id) || '',
                item_name: (pageContext.product && pageContext.product.title) || '',
                video_type: 'youtube'
              });
            }

            // Vimeo
            if (data.event === 'play') {
              self.pushCustomEvent('video_play', {
                item_id: (pageContext.product && pageContext.product.id) || '',
                item_name: (pageContext.product && pageContext.product.title) || '',
                video_type: 'vimeo'
              });
            }
          } catch (err) {
            // Not a video message
          }
        });
      }

      // ----- Product Tabs/Accordions -----
      if (config.tracking.productTabs) {
        document.addEventListener('toggle', function(e) {
          if (!e.target.matches('details.product-accordion, details.collapsible-tabs__item')) return;
          if (!e.target.open) return;

          const tabName = self.getText(e.target.querySelector('summary'));

          self.pushCustomEvent('product_tab_view', {
            tab_name: tabName,
            item_id: (pageContext.product && pageContext.product.id) || '',
            item_name: (pageContext.product && pageContext.product.title) || ''
          });
        }, true);
      }

      // ----- Reviews Viewed -----
      if (config.tracking.reviewsViewed) {
        let reviewsTracked = false;
        var reviewsObserver = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting && !reviewsTracked) {
              reviewsTracked = true;

              self.pushCustomEvent('reviews_viewed', {
                item_id: (pageContext.product && pageContext.product.id) || '',
                item_name: (pageContext.product && pageContext.product.title) || ''
              });

              reviewsObserver.disconnect();
            }
          });
        }, { threshold: 0.5 });

        const reviewsSection = document.querySelector('#reviews, .product-reviews, [data-reviews-section], .stamped-reviews, .yotpo-reviews');
        if (reviewsSection) {
          reviewsObserver.observe(reviewsSection);
        }
      }
    },

    // ============================================================================
    // TIER 4: CART & PROMO ENGAGEMENT
    // ============================================================================

    setupTier4CartPromo: function() {
      const self = this;

      // ----- Cart Drawer Open -----
      if (config.tracking.cartDrawer) {
        let cartDrawerTracked = false;
        const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');

        if (cartDrawer) {
          const cartDrawerObserver = new MutationObserver(function(mutations) {
            if (cartDrawer.classList.contains('active') || cartDrawer.hasAttribute('open')) {
              if (!cartDrawerTracked) {
                cartDrawerTracked = true;

                self.pushCustomEvent('view_cart', {
                  cart_source: 'drawer'
                });

                setTimeout(function() { cartDrawerTracked = false; }, 2000);
              }
            }
          });

          cartDrawerObserver.observe(cartDrawer, {
            attributes: true,
            attributeFilter: ['class', 'open']
          });
        }
      }

      // ----- Gift Wrap Toggle -----
      if (config.tracking.giftWrap) {
        document.addEventListener('change', function(e) {
          const checkbox = safeClosest(e.target, '[data-gift-wrap], .gift-wrapping-checkbox, input[name*="gift"]');
          if (!checkbox || checkbox.type !== 'checkbox') return;

          self.pushCustomEvent('gift_wrap_toggle', {
            enabled: checkbox.checked
          });
        });
      }

      // ----- Discount Code Applied -----
      if (config.tracking.discountCode) {
        document.addEventListener('submit', function(e) {
          const form = safeClosest(e.target, '[action*="/discount"], .cart-discount form, form[data-discount-form]');
          if (!form) return;

          const codeInput = form.querySelector('input[name="discount"], input[name="code"]');
          if (codeInput && codeInput.value) {
            self.pushCustomEvent('coupon_applied', {
              coupon_code: codeInput.value
            });
          }
        });
      }

      // ----- Free Shipping Progress -----
      if (config.tracking.freeShipping && typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        let lastShippingProgress = null;

        FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          function(data) {
            const progressBar = document.querySelector('.free-shipping-goal, [data-free-shipping-goal]');
            if (!progressBar) return;

            const threshold = parseFloat(progressBar.dataset.threshold) || 0;
            const cartTotal = (data && data.items_subtotal_price) ? data.items_subtotal_price / 100 : 0;
            const remaining = Math.max(0, threshold - cartTotal);
            const progress = Math.min(100, (cartTotal / threshold) * 100);

            // Only track significant changes (crossed thresholds)
            const progressBracket = Math.floor(progress / 25) * 25;
            if (progressBracket !== lastShippingProgress && progressBracket > 0) {
              lastShippingProgress = progressBracket;

              self.pushCustomEvent('shipping_progress', {
                progress_percent: progressBracket,
                amount_remaining: remaining.toFixed(2),
                threshold: threshold
              });
            }
          }
        );
      }

      // ----- Popup View -----
      if (config.tracking.popup) {
        const popupObserver = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            const popup = mutation.target.closest('.popup-modal, [data-popup]');
            if (popup && (popup.classList.contains('is-visible') || popup.hasAttribute('open'))) {
              self.pushCustomEvent('popup_view', {
                popup_id: popup.id || popup.dataset.popupId || 'unknown'
              });
            }
          });
        });

        const popups = document.querySelectorAll('.popup-modal, [data-popup]');
        popups.forEach(function(popup) {
          popupObserver.observe(popup, { attributes: true, attributeFilter: ['class', 'open'] });
        });
      }

      // ----- Announcement Click -----
      if (config.tracking.announcement) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.announcement-bar a, .announcement-bar__link');
          if (!link) return;

          self.pushCustomEvent('announcement_click', {
            announcement_text: self.getText(link.closest('.announcement-bar__message')),
            link_url: link.href
          });
        });
      }
    },

    // ============================================================================
    // TIER 5: NAVIGATION & DISCOVERY
    // ============================================================================

    setupTier5NavigationDiscovery: function() {
      const self = this;

      // ----- Hero Banner Clicks -----
      if (config.tracking.heroBanner) {
        document.addEventListener('click', function(e) {
          // Hero banner links and buttons
          const link = safeClosest(e.target, '.image-with-text-overlay a, .image-with-text-ovelay__content a, .hero-banner a, [class*="hero"] .btn, [class*="hero"] a[href]');
          if (!link) return;

          // Get banner context
          const section = link.closest('[class*="section"], [data-section]');
          const sectionId = section ? (section.dataset.section || section.dataset.id || section.id) : '';
          const heading = self.getText(section && section.querySelector('h1, h2, .rich-text__heading'));
          const buttonText = self.getText(link);

          self.pushCustomEvent('hero_banner_click', {
            banner_section_id: sectionId,
            banner_heading: heading,
            click_text: buttonText,
            click_url: link.href,
            page_template: pageContext.template
          });
        });
      }

      // ----- Promotion Banner Clicks -----
      if (config.tracking.promoBanner) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.promotion-banner a, .promotion-banner__inner a');
          if (!link) return;

          const section = link.closest('.promotion-banner, [data-section]');
          const heading = self.getText(section && section.querySelector('h2, .rich-text__heading'));
          const subheading = self.getText(section && section.querySelector('.text-subheading, .rich-text__subheading'));

          self.pushCustomEvent('promo_banner_click', {
            promo_heading: heading,
            promo_subheading: subheading,
            click_text: self.getText(link),
            click_url: link.href,
            page_template: pageContext.template
          });
        });
      }

      // ----- Grid Banner Clicks -----
      if (config.tracking.gridBanner) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.grid-banner__item a, .grid-banner a.card-media');
          if (!link) return;

          const card = link.closest('.grid-banner__item') || link;
          const heading = self.getText(card.querySelector('h2, h3, .card-media__heading'));
          const index = Array.from(card.parentElement.children).indexOf(card);

          self.pushCustomEvent('grid_banner_click', {
            banner_heading: heading,
            banner_position: index + 1,
            click_url: link.href,
            page_template: pageContext.template
          });
        });
      }

      // ----- Collection Tiles/Cards Clicks -----
      if (config.tracking.collectionTiles) {
        document.addEventListener('click', function(e) {
          // Collection list items, collection tiles, collection cards
          const link = safeClosest(e.target, '.collection-card a, .collection-list a[href*="/collections/"], .collection-tiles a, collection-list-v2-slider a[href*="/collections/"], .card-collection a');
          if (!link) return;

          // Don't track if it's a product link (those are handled separately)
          if (link.href.includes('/products/')) return;

          const card = link.closest('.collection-card, .card-collection, .swiper-slide, [class*="collection"]');
          const collectionTitle = self.getText(card && card.querySelector('.card-collection__title, .collection-card__title, h3, h4'));

          // Extract collection handle from URL
          const match = link.href.match(/\/collections\/([^/?#]+)/);
          const collectionHandle = match ? match[1] : '';

          self.pushCustomEvent('collection_tile_click', {
            collection_title: collectionTitle,
            collection_handle: collectionHandle,
            click_url: link.href,
            page_template: pageContext.template,
            source_location: self.getSourceLocation(link)
          });
        });
      }

      // ----- Collection Banner Clicks (on collection pages) -----
      if (config.tracking.collectionBanner) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.collection-hero a, .main-collection-banner a, [class*="collection-banner"] a');
          if (!link) return;

          self.pushCustomEvent('collection_banner_click', {
            collection_handle: (pageContext.collection && pageContext.collection.handle) || '',
            collection_title: (pageContext.collection && pageContext.collection.title) || '',
            click_text: self.getText(link),
            click_url: link.href
          });
        });
      }

      // ----- Image With Text Section Clicks -----
      if (config.tracking.imageWithText) {
        document.addEventListener('click', function(e) {
          const link = safeClosest(e.target, '.image-with-text a, .image-with-text-slider a, .lookbook-banner a, .lookbook-slider a');
          if (!link) return;

          // Avoid double-tracking hero banners
          if (link.closest('.image-with-text-overlay, .image-with-text-ovelay__content')) return;

          const section = link.closest('[class*="section"], [data-section]');
          const heading = self.getText(section && section.querySelector('h2, h3, .rich-text__heading'));

          self.pushCustomEvent('image_section_click', {
            section_heading: heading,
            click_text: self.getText(link),
            click_url: link.href,
            page_template: pageContext.template
          });
        });
      }

      // ----- Slider Navigation Tracking -----
      if (config.tracking.sliderNavigation) {
        // Track slider arrow/dot clicks
        document.addEventListener('click', function(e) {
          const navBtn = safeClosest(e.target, '.swiper-button-next, .swiper-button-prev, .swiper-pagination-bullet, [class*="slider"] .arrow');
          if (!navBtn) return;

          const slider = navBtn.closest('.swiper, [class*="slider"], [class*="carousel"]');
          if (!slider) return;

          const section = slider.closest('[class*="section"], [data-section]');
          const sectionType = self.getSectionType(section);
          const direction = navBtn.classList.contains('swiper-button-next') || navBtn.classList.contains('arrow-next') ? 'next' : 'prev';

          self.pushCustomEvent('slider_navigation', {
            section_type: sectionType,
            direction: navBtn.classList.contains('swiper-pagination-bullet') ? 'dot' : direction,
            page_template: pageContext.template
          });
        });
      }

      // Log tier 5 initialization
      this.log('Tier 5: Navigation & Discovery tracking initialized');
    },

    /**
     * Helper: Get source location description for analytics
     */
    getSourceLocation: function(element) {
      const section = element.closest('[class*="section"]');
      if (!section) return 'unknown';

      // Try to identify section type from class names
      const classList = section.className;
      if (classList.includes('collection-list')) return 'collection_list';
      if (classList.includes('collection-tiles')) return 'collection_tiles';
      if (classList.includes('featured-collection')) return 'featured_collection';
      if (classList.includes('grid-banner')) return 'grid_banner';
      if (classList.includes('hero')) return 'hero_banner';
      if (classList.includes('promotion')) return 'promotion_banner';

      return 'section';
    },

    /**
     * Helper: Get section type from element
     */
    getSectionType: function(section) {
      if (!section) return 'unknown';

      const classList = section.className || '';
      if (classList.includes('hero')) return 'hero_slider';
      if (classList.includes('collection')) return 'collection_slider';
      if (classList.includes('product')) return 'product_slider';
      if (classList.includes('testimonial')) return 'testimonial_slider';
      if (classList.includes('image-with-text')) return 'image_text_slider';
      if (classList.includes('lookbook')) return 'lookbook_slider';

      return 'slider';
    }
  };

  // ============================================================================
  // MENU V5 TRACKING (visits)
  // ============================================================================

  /**
   * Menu v5 (#1910). menu-v5.js reports what happened as bubbling menu-v5:* events; this
   * decides what counts as a visit and sends menu_open, menu_drill, menu_click and
   * menu_abandon. R1-R16 are the rules in issue #1910.
   *
   * A visit is pending (pointer on the nav, not counted yet), counted, closing (pointer
   * left a hover visit, 600ms to come back) or uncounted (restored from the back/forward
   * cache with a menu open; sends nothing until it closes). The events only fire under
   * v5, so nothing here needs the version check.
   */
  function setupMenuV5Tracking() {
    const HOVER_OPEN_MS = 900;
    const LEAVE_GRACE_MS = 600;
    const VIEWED_MS = 300;

    let visitCount = 0;
    let visit = null;
    let panel = null;
    let viewTimer = null;

    // R11, R15: time on performance.now(), minus any time the page was hidden.
    let hiddenTotal = 0;
    let hiddenSince = document.hidden ? performance.now() : null;
    document.addEventListener('visibilitychange', function() {
      const now = performance.now();
      if (document.hidden) {
        if (hiddenSince === null) hiddenSince = now;
      } else if (hiddenSince !== null) {
        hiddenTotal += now - hiddenSince;
        hiddenSince = null;
      }
    });
    function clock() {
      const now = performance.now();
      return now - hiddenTotal - (hiddenSince === null ? 0 : now - hiddenSince);
    }

    function send(name, surface, visitId, params) {
      params.menu_version = 'v5';
      params.menu_surface = surface;
      params.visit_id = visitId;
      params.page_template = pageContext.template;
      GA4CustomEvents.pushCustomEvent(name, params);
    }

    function startVisit(surface, state) {
      visit = {
        state: state || 'pending',
        surface: surface,
        start: clock(),
        id: 0,
        dept: null,
        viewed: {},
        deptsViewed: 0,
        lastViewed: null,
        clicks: 0,
        leftAt: null,
        timer: null,
        due: false
      };
    }

    // R7, R13: the id is given when the visit counts, so ids go out in order.
    function count(method, dept) {
      clearTimeout(visit.timer);
      visit.state = 'counted';
      visit.id = ++visitCount;
      visit.dept = dept;
      send('menu_open', visit.surface, visit.id, { menu_dept: dept, open_method: method });
    }

    // R10: an abandon for a counted visit that ends without a click.
    function endVisit(method, endedAt) {
      const ended = visit;
      visit = null;
      clearTimeout(ended.timer);
      if ((ended.state !== 'counted' && ended.state !== 'closing') || ended.clicks) return;
      send('menu_abandon', ended.surface, ended.id, {
        menu_dept: ended.lastViewed || ended.dept,
        close_method: method,
        depts_viewed: ended.deptsViewed,
        time_open_ms: Math.round(endedAt - ended.start)
      });
    }

    function resumeVisit() {
      clearTimeout(visit.timer);
      visit.state = 'counted';
      visit.leftAt = null;
    }

    // R8: a department counts as viewed once its panel or drill screen has shown for 300ms.
    function watch(dept) {
      clearTimeout(viewTimer);
      viewTimer = setTimeout(function() {
        if (!visit || visit.state === 'uncounted') return;
        if (!visit.viewed[dept]) {
          visit.viewed[dept] = true;
          visit.deptsViewed += 1;
        }
        visit.lastViewed = dept;
      }, VIEWED_MS);
    }

    function on(name, handler) {
      document.addEventListener('menu-v5:' + name, function(event) {
        handler(event.detail || {});
      });
    }

    // R2, R3: a hover visit starts at the first tab and counts at 900ms with a panel
    // showing, or at the first panel after that while the pointer stays on the nav.
    on('tab-enter', function() {
      if (visit) return;
      startVisit('desktop_panel');
      const pending = visit;
      pending.timer = setTimeout(function() {
        if (visit !== pending || pending.state !== 'pending') return;
        if (panel) count('hover', panel.dept);
        else pending.due = true;
      }, HOVER_OPEN_MS);
    });

    on('region-enter', function() {
      if (visit && visit.state === 'closing') resumeVisit();
    });

    // R4, R9: leaving drops a pending visit and starts the 600ms window on a hover one. A
    // panel opened by click, tap or keyboard stays open, so its visit waits for the close,
    // and so does a hover panel whose ▾ was clicked (panel-pinned). A panel still showing
    // when the window runs out is treated the same way.
    on('region-leave', function() {
      if (!visit || visit.surface !== 'desktop_panel') return;
      if (visit.state === 'pending') {
        clearTimeout(visit.timer);
        visit = null;
        return;
      }
      if (visit.state !== 'counted' || (panel && panel.trigger !== 'hover')) return;
      const closing = visit;
      closing.state = 'closing';
      closing.leftAt = clock();
      closing.timer = setTimeout(function() {
        if (visit !== closing || closing.state !== 'closing') return;
        if (panel) resumeVisit();
        else endVisit('mouseleave', closing.leftAt);
      }, LEAVE_GRACE_MS);
    });

    // R5, R6: click, tap, keyboard and the phone drawer count right away.
    on('panel-shown', function(detail) {
      if (detail.trigger === 'editor') return;
      if (detail.surface === 'phone_drawer') {
        if (!visit) {
          startVisit('phone_drawer');
          count(detail.trigger, detail.dept);
        }
        return;
      }
      panel = { dept: detail.dept, trigger: detail.trigger };
      watch(detail.dept);
      if (!visit) {
        if (detail.trigger === 'hover') return;
        startVisit('desktop_panel');
        count(detail.trigger, detail.dept);
        return;
      }
      if (visit.state === 'closing') resumeVisit();
      if (visit.state !== 'pending') return;
      if (detail.trigger !== 'hover') count(detail.trigger, detail.dept);
      else if (visit.due) count('hover', detail.dept);
    });

    // R5: clicking the ▾ of a panel hover opened keeps it open, the same as a click open.
    // A visit still pending counts now, and the panel stops ending with the pointer.
    on('panel-pinned', function(detail) {
      if (panel) panel.trigger = detail.trigger;
      if (!visit) {
        startVisit('desktop_panel');
        count(detail.trigger, detail.dept);
        return;
      }
      if (visit.surface !== 'desktop_panel') return;
      if (visit.state === 'closing') resumeVisit();
      else if (visit.state === 'pending') count(detail.trigger, detail.dept);
    });

    // R9: the close that leaves nothing open ends the visit. A switch never does, and a
    // hover panel closing on mouseleave leaves it to the 600ms window.
    on('panel-hidden', function(detail) {
      if (detail.reason === 'editor') return;
      clearTimeout(viewTimer);
      if (detail.surface === 'phone_drawer') {
        if (visit && visit.surface === 'phone_drawer') endVisit(detail.reason, clock());
        return;
      }
      panel = null;
      if (detail.reason === 'switch' || !visit || visit.surface !== 'desktop_panel') return;
      if (visit.state === 'uncounted') endVisit(detail.reason, clock());
      else if (visit.state === 'counted' && detail.reason !== 'mouseleave') endVisit(detail.reason, clock());
    });

    on('screen-shown', function(detail) {
      watch(detail.dept);
      if (visit && visit.surface === 'phone_drawer' && visit.state === 'counted') {
        send('menu_drill', 'phone_drawer', visit.id, { menu_dept: detail.dept });
      }
    });

    on('screen-hidden', function() {
      clearTimeout(viewTimer);
    });

    // R12: a click in a pending visit with a panel showing counts the visit first. A click
    // with no visit to join (a tab name before any panel shows) is a one-click visit, and
    // leaves time_to_click_ms out: nothing was timed, and a 0 would drag its averages down.
    on('link', function(detail) {
      if (visit && visit.state === 'uncounted') return;
      if (visit && visit.state === 'pending' && panel) count('hover', panel.dept);
      const joined = visit && visit.state !== 'pending' ? visit : null;
      if (joined) joined.clicks += 1;
      const params = {
        menu_dept: detail.dept,
        menu_section: detail.section,
        click_text: String(detail.label || '').slice(0, 100),
        menu_position: detail.position,
        click_url: detail.url,
        visit_click_index: joined ? joined.clicks : 1,
        depts_viewed: joined ? joined.deptsViewed : 0
      };
      if (joined) params.time_to_click_ms = Math.round(clock() - joined.start);
      send('menu_click', detail.surface, joined ? joined.id : ++visitCount, params);
    });

    // R14: leaving the page ends a counted visit. gtag.js sends with fetch keepalive (or
    // sendBeacon) and flushes on pagehide itself; GA4 has no transport_type setting. A page
    // restored from the back/forward cache picks up whatever menu is still open as an
    // uncounted visit; menu-v5.js then closes it (reason restore), which sends nothing.
    window.addEventListener('pagehide', function() {
      clearTimeout(viewTimer);
      panel = null;
      if (!visit) return;
      endVisit('pagehide', visit.state === 'closing' ? visit.leftAt : clock());
    });

    window.addEventListener('pageshow', function(event) {
      if (!event.persisted) return;
      const openPanel = document.querySelector('menu-v5-nav .menu-v5__panel:not([hidden])');
      const drawer = document.querySelector('menu-v5-drawer');
      const shell = drawer && drawer.closest('menu-drawer');
      if (openPanel) {
        panel = { dept: openPanel.dataset.menuDept, trigger: 'restore' };
        startVisit('desktop_panel', 'uncounted');
      } else if (shell && shell.open) {
        startVisit('phone_drawer', 'uncounted');
      }
    });
  }

  if (!config.designMode) {
    setupMenuV5Tracking();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Defer init to idle (parity with klaviyo-enhanced.liquid) so registering the
  // ~25 tracking listeners doesn't compete with hydration on the input-heavy
  // collection grid. Still runs after DOM ready and well before the first
  // interaction (Algolia bootstraps async), so no events are lost.
  function bootGA4CustomEvents() {
    (window.requestIdleCallback || function(cb) { setTimeout(cb, 1); })(function() {
      GA4CustomEvents.init();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGA4CustomEvents);
  } else {
    bootGA4CustomEvents();
  }

  // Expose for debugging
  window.GA4CustomEvents = GA4CustomEvents;

})();
